import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import workforceManagementService from '../../services/workforceManagementService';
import AddEmployeeModal from './components/AddEmployeeModal';
import EmployeeTable from './components/EmployeeTable';
import SubscriptionCard from './components/SubscriptionCard';
import AuditLogsList from './components/AuditLogsList';
import LiveUpdatesSection from './components/LiveUpdatesSection';
import EmptyStatePrompt from '../../components/EmptyStatePrompt';
import TeaserEditModal from '../../components/TeaserEditModal';
import { useTeaserEdit } from '../../hooks/useTeaserEdit';
import { billingService } from '../../services/billingService';
import { supabase } from '../../lib/supabase';
import HRLetterApprovalPanel from '../employee-self-service-portal/components/HRLetterApprovalPanel';
import { useCompanySettings } from '../../contexts/CompanySettingsContext';


import realtimeDashboardService from '../../services/realtimeDashboardService';

const WorkforceManagementDashboard = () => {
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [globalFilter, setGlobalFilter] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState('active');
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState('current_month');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [success, setSuccess] = useState('');
  const [user, setUser] = useState(null);
  const [isHRManager, setIsHRManager] = useState(false);

  const { companySettings } = useCompanySettings();

  const { 
    editingField: teaserEditField, 
    showRedirectModal, 
    startEdit, 
    handleInput: handleTeaserInput, 
    closeModal,
    isEditable 
  } = useTeaserEdit(subscriptionStatus === 'active');

  useEffect(() => {
    loadData();
    checkSubscription();
    const fetchUser = async () => {
      const { data: { user } } = await supabase?.auth?.getUser();
      setUser(user);
      
      // Check if user is HR Manager
      if (user?.email) {
        const { data: employee } = await supabase
          ?.from('employees')
          ?.select('position')
          ?.eq('email', user?.email)
          ?.single();
        
        if (employee?.position?.toLowerCase()?.includes('hr manager')) {
          setIsHRManager(true);
        }
      }
    };
    fetchUser();

    // Subscribe to real-time workforce updates
    const channels = realtimeDashboardService?.subscribeToAllUpdates(
      {
        onViolation: (violation) => {
          loadData();
        },
        onPayrollError: (error) => {
          loadData();
        },
        onDeadline: (deadline) => {
          loadData();
        }
      },
      'all'
    );

    return () => {
      realtimeDashboardService?.unsubscribeAll(channels);
    };
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const companyData = await workforceManagementService?.getActiveCompany();
      setCompany(companyData);

      if (companyData?.id) {
        const [subscriptionData, employeesData, tiersData, logsData] = await Promise.all([
          workforceManagementService?.getCompanySubscription(companyData?.id),
          workforceManagementService?.getCompanyEmployees(companyData?.id),
          workforceManagementService?.getSubscriptionTiers(),
          workforceManagementService?.getWorkforceAuditLogs(companyData?.id, 20)
        ]);

        setSubscription(subscriptionData);
        setEmployees(employeesData);
        setTiers(tiersData);
        setAuditLogs(logsData);
      }
    } catch (err) {
      setError(err?.message || 'Failed to load workforce data');
    } finally {
      setLoading(false);
    }
  };

  const checkSubscription = async () => {
    try {
      const { data: user } = await supabase?.auth?.getUser();
      if (user?.user?.id) {
        const { data: companySettings } = await supabase?.from('company_settings')?.select('id')?.eq('user_id', user?.user?.id)?.single();
        
        if (companySettings?.id) {
          const subscriptionDetails = await billingService?.getSubscriptionDetails(companySettings?.id);
          const status = subscriptionDetails?.subscriptionStatus || 'suspended';
          setSubscriptionStatus(status);
        }
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  const handleAddEmployee = async (employeeData) => {
    try {
      await workforceManagementService?.addEmployee(company?.id, employeeData);
      setShowAddModal(false);
      await loadData();
    } catch (err) {
      throw new Error(err?.message || 'Failed to add employee');
    }
  };

  const handleRemoveEmployee = async (employeeId) => {
    if (!window.confirm('Are you sure you want to remove this employee? This action cannot be undone.')) {
      return;
    }

    try {
      await workforceManagementService?.removeEmployee(employeeId);
      await loadData();
    } catch (err) {
      setError(err?.message || 'Failed to remove employee');
    }
  };

  const handleToggleEmployeeStatus = async (employeeId, currentStatus) => {
    try {
      await workforceManagementService?.updateEmployeeStatus(employeeId, !currentStatus);
      await loadData();
    } catch (err) {
      setError(err?.message || 'Failed to update employee status');
    }
  };

  const handleRemoveAllEmployees = async () => {
    if (!window.confirm('WARNING: This will remove ALL employees from the system. This action cannot be undone. Are you sure?')) {
      return;
    }

    try {
      await workforceManagementService?.removeAllEmployees(company?.id);
      await loadData();
    } catch (err) {
      setError(err?.message || 'Failed to remove all employees');
    }
  };

  const handleFilterChange = (filters) => {
    setGlobalFilter(filters);
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'departments', label: 'Departments', icon: 'Building2' },
    { id: 'employees', label: 'Employees', icon: 'Users' },
    { id: 'analytics', label: 'Analytics', icon: 'TrendingUp' },
  ];

  // Add letter approvals tab for HR managers
  if (isHRManager) {
    tabs?.push({ id: 'letter_approvals', label: 'Letter Approvals', icon: 'Mail' });
  }

  const activeEmployees = employees?.filter(e => e?.isActive) || [];
  const inactiveEmployees = employees?.filter(e => !e?.isActive) || [];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading workforce data...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show empty state if no employees
  if (!employees || employees?.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-20 pb-12 px-4 md:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="mb-6 md:mb-8">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-2">
              Workforce Management
            </h1>
            <p className="caption text-sm md:text-base text-muted-foreground">
              Manage your workforce and monitor subscription billing
            </p>
          </div>

          {/* Billing Activation Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
            <div className="flex items-start gap-4">
              <Icon name="Info" className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-blue-900 mb-2">Billing Tier Activation</h3>
                <p className="text-sm text-blue-800 mb-3">
                  Your subscription billing will be automatically activated once you add your first employee. 
                  The billing tier will be determined based on your total employee count.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-4">
                  <div className="bg-white rounded-md p-3 border border-blue-100">
                    <p className="text-xs font-medium text-blue-900 mb-1">
                      1-10 Employees
                    </p>
                    <p className="text-lg font-bold text-blue-600">
                      K500/mo
                    </p>
                  </div>
                  <div className="bg-white rounded-md p-3 border border-blue-100">
                    <p className="text-xs font-medium text-blue-900 mb-1">
                      11-50 Employees
                    </p>
                    <p className="text-lg font-bold text-blue-600">
                      K1,500/mo
                    </p>
                  </div>
                  <div className="bg-white rounded-md p-3 border border-blue-100">
                    <p className="text-xs font-medium text-blue-900 mb-1">
                      51-500 Employees
                    </p>
                    <p className="text-lg font-bold text-blue-600">
                      K4,500/mo
                    </p>
                  </div>
                  <div className="bg-white rounded-md p-3 border border-blue-100">
                    <p className="text-xs font-medium text-blue-900 mb-1">
                      501+ Employees
                    </p>
                    <p className="text-lg font-bold text-blue-600">
                      K13,500/mo
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-card rounded-lg border border-border p-8">
            <EmptyStatePrompt
              icon="Users"
              title="Build Your Workforce"
              description="Add your first employee to activate compliance tracking, payroll management, and automated reporting. Your billing tier will be automatically assigned based on employee count."
              actionLabel="Add First Employee"
              onAction={() => setShowAddModal(true)}
            />
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-card rounded-lg border border-border p-6">
              <Icon name="TrendingUp" className="w-8 h-8 text-primary mb-3" />
              <h3 className="font-semibold text-foreground mb-2">Automatic Tier Management</h3>
              <p className="text-sm text-muted-foreground">
                Your subscription tier automatically adjusts as your workforce grows
              </p>
            </div>
            <div className="bg-card rounded-lg border border-border p-6">
              <Icon name="Shield" className="w-8 h-8 text-primary mb-3" />
              <h3 className="font-semibold text-foreground mb-2">Compliance Monitoring</h3>
              <p className="text-sm text-muted-foreground">
                Real-time tracking of PNG Employment Act adherence
              </p>
            </div>
            <div className="bg-card rounded-lg border border-border p-6">
              <Icon name="FileText" className="w-8 h-8 text-primary mb-3" />
              <h3 className="font-semibold text-foreground mb-2">Audit Trail</h3>
              <p className="text-sm text-muted-foreground">
                Complete history of workforce changes and billing updates
              </p>
            </div>
          </div>
        </main>

        {showAddModal && (
          <AddEmployeeModal
            companyId={company?.id}
            onClose={() => setShowAddModal(false)}
            onSubmit={handleAddEmployee}
          />
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <ComplianceAlertBanner />
      <GlobalFilterBar onFilterChange={handleFilterChange} />
      <DataRefreshIndicator />
      <main className="pt-20 pb-12 px-4 md:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Workforce Management Dashboard</h1>
          <p className="text-muted-foreground">Manage workforce and subscription billing</p>
        </div>

        {error && (
          <div className="bg-error/10 border border-error text-error px-4 py-3 rounded-md mb-6">
            <p className="font-medium">Error</p>
            <p className="text-sm">{error}</p>
          </div>
        )}

        {/* Subscription Card */}
        {subscription && (
          <SubscriptionCard 
            subscription={subscription} 
            tiers={tiers}
            companyName={company?.companyName}
          />
        )}

        {/* Live Updates & News Section */}
        <LiveUpdatesSection />

        {/* Employee Management Section */}
        <div className="bg-card rounded-lg shadow-sm border border-border p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-foreground mb-1">Employee Management</h2>
              <p className="text-sm text-muted-foreground">
                {activeEmployees?.length} active employees, {inactiveEmployees?.length} inactive
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRemoveAllEmployees}
                className="text-error border-error hover:bg-error/10"
              >
                <Icon name="Trash2" size={16} className="mr-2" />
                Remove All
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={() => setShowAddModal(true)}
              >
                <Icon name="Plus" size={16} className="mr-2" />
                Add Employee
              </Button>
            </div>
          </div>

          <EmployeeTable
            employees={employees}
            onRemove={handleRemoveEmployee}
            onToggleStatus={handleToggleEmployeeStatus}
          />
        </div>

        {/* Audit Logs Section */}
        <div className="bg-card rounded-lg shadow-sm border border-border p-6">
          <h2 className="text-xl font-bold text-foreground mb-4">Compliance Audit Logs</h2>
          <AuditLogsList logs={auditLogs} />
        </div>

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            <div className="bg-card border border-border rounded-lg p-8 text-center">
              <Icon name="TrendingUp" size={48} className="text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">Analytics Dashboard</h3>
              <p className="text-muted-foreground">
                Workforce analytics and insights coming soon.
              </p>
            </div>
          </div>
        )}

        {/* Letter Approvals Tab (HR Managers Only) */}
        {activeTab === 'letter_approvals' && isHRManager && (
          <HRLetterApprovalPanel />
        )}
      </main>

      {showAddModal && (
        <AddEmployeeModal
          companyId={company?.id}
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAddEmployee}
        />
      )}

      <TeaserEditModal isOpen={showRedirectModal} onClose={closeModal} />
    </div>
  );
};

export default WorkforceManagementDashboard;